import { useState, useEffect, useRef, ChangeEvent } from 'react';

import { CartSnapshot, RecipientFormData, ShareSubmitData } from '../types/sharedBasket.types';

import EmailForm from './EmailForm';

interface UserDetails {
  firstName?: string;
  lastName?: string;
  email?: string;
}

interface ShareBasketModalProps {
  isOpen: boolean;
  onClose: () => void;
  basketData?: CartSnapshot | null;
  userDetails?: UserDetails | null;
  onShare: (data: ShareSubmitData) => Promise<void>;
  onSuccess?: (recipientName: string) => void;
  onError?: (message: string) => void;
}

type FormErrors = Partial<Record<keyof RecipientFormData, string>>;

const EMPTY_FORM: RecipientFormData = { firstName: '', lastName: '', email: '' };

const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const validateField = (name: keyof RecipientFormData, value: string): string => {
  switch (name) {
    case 'firstName':
      return value.trim() === '' ? 'First name is required' : '';
    case 'lastName':
      return value.trim() === '' ? 'Last name is required' : '';
    case 'email':
      if (value.trim() === '') return 'Email address is required';
      if (!isValidEmail(value)) return 'Please enter a valid email address';
      return '';
  }
};

const ShareBasketModal = ({
  isOpen,
  onClose,
  basketData,
  userDetails,
  onShare,
  onSuccess,
  onError,
}: ShareBasketModalProps) => {
  const [formData, setFormData] = useState<RecipientFormData>(EMPTY_FORM);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAllErrors, setShowAllErrors] = useState(false);

  /**
   * Stable ref relay for userDetails.
   * The effect intentionally only fires when the modal opens (isOpen changes),
   * not on every userDetails change — so we read from a ref to avoid adding
   * userDetails to the dep array and accidentally resetting user edits.
   */
  const userDetailsRef = useRef(userDetails);
  useEffect(() => {
    userDetailsRef.current = userDetails;
  }, [userDetails]);

  useEffect(() => {
    if (isOpen && userDetailsRef.current) {
      setFormData((prev) => ({
        ...prev,
        firstName: userDetailsRef.current?.firstName ?? prev.firstName,
        lastName: userDetailsRef.current?.lastName ?? prev.lastName,
        email: userDetailsRef.current?.email ?? prev.email,
      }));
    }
  }, [isOpen]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    const error = validateField(name as keyof RecipientFormData, value);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {
      firstName: validateField('firstName', formData.firstName),
      lastName: validateField('lastName', formData.lastName),
      email: validateField('email', formData.email),
    };
    setErrors(newErrors);
    return Object.values(newErrors).every((err) => err === '');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setShowAllErrors(true);

    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const shareData: ShareSubmitData = {
        recipient: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
        },
        basket: basketData ?? null,
      };

      await onShare(shareData);

      const recipientName = `${formData.firstName} ${formData.lastName}`.trim();
      setFormData(EMPTY_FORM);
      onClose();
      onSuccess?.(recipientName);
    } catch (error) {
      onError?.((error instanceof Error ? error.message : null) ?? 'Failed to share basket. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-level2 w-full max-w-[550px] border border-[#D0D5DD]">
          {/* Close button */}
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="absolute top-4 right-4 text-[#667085] hover:text-gray-800 focus:outline-none disabled:opacity-50 z-10"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Modal content */}
          <div className="p-6 sm:p-8 flex flex-col gap-3">
            <div>
              <h2 className="font-aleo text-2xl sm:text-3xl font-semibold text-gray-900 mb-2">
                Share your basket
              </h2>
              <p className="text-sm text-gray-600">
                Save the basket&apos;s contents and send it to yourself or share it with someone else.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-[20px]">
              <EmailForm
                formData={formData}
                onChange={handleChange}
                errors={errors}
                showAllErrors={showAllErrors}
              />

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isSubmitting}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-6 py-2 bg-black text-white rounded-md hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Sending...' : 'Share basket'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareBasketModal;
